import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Calendar, Clock, Settings, Save, Plus, X, Trash2, RotateCcw, Users } from 'lucide-react';
import { toast } from 'sonner';
import EventScheduleSection from './EventScheduleSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const DEFAULT_SLOTS = [
  '11:00 - 11:45',
  '11:15 - 12:00',
  '11:45 - 12:30',
  '12:00 - 12:45',
  '12:30 - 13:15',
  '12:45 - 13:30',
  '13:15 - 14:00',
  '13:30 - 14:15',
  '14:00 - 14:45',
  '14:15 - 15:00',
  '14:45 - 15:30',
  '15:00 - 15:45',
];

export default function EventSettings() {
  const { getAuthHeader, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState({
    event_date: '',
    event_time: '',
    venue_name: '',
    venue_url: '',
    default_slot_capacity: 5,
    time_slots: [],
  });
  // Live next-event from /event-settings/next (mirrors the Schedule tab and
  // public form/home). Shown read-only on this tab so admins always see the
  // same date/venue the public site displays.
  const [nextEvent, setNextEvent] = useState(null);
  const [newSlot, setNewSlot] = useState('');

  useEffect(() => {
    if (user?.role === 'super_admin') {
      bootstrap();
    }
  }, [user]);

  const bootstrap = async () => {
    try {
      setLoading(true);
      const [settingsRes, catsRes, nextRes] = await Promise.all([
        axios.get(`${API}/event-settings`, { headers: getAuthHeader() }).catch((err) => {
          if (err.response?.status === 404) return { data: null };
          throw err;
        }),
        axios.get(`${API}/categories?active_only=true`),
        axios.get(`${API}/event-settings/next`, { headers: getAuthHeader() }).catch(() => ({ data: null })),
      ]);
      const activeCats = catsRes.data || [];
      setCategories(activeCats);
      setNextEvent(nextRes.data);
      if (settingsRes.data) {
        setSettings((prev) => ({
          ...prev,
          ...settingsRes.data,
        }));
      }
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Event date/venue/url come from the Schedule tab (next-event auto-promotion).
      // We send the schedule's next event values so the singleton stays in sync,
      // falling back to existing settings if no future event is scheduled.
      const payload = {
        ...settings,
        event_date: nextEvent?.event_date || settings.event_date || 'To be announced',
        venue_name: nextEvent?.venue_name || settings.venue_name || 'Peterborough Repair Café',
        venue_url: nextEvent?.venue_url || settings.venue_url || '',
      };
      await axios.put(`${API}/event-settings`, payload, { headers: getAuthHeader() });
      toast.success('Settings saved successfully!');
      bootstrap();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addTimeSlot = () => {
    const cleaned = newSlot.trim();
    if (!cleaned) {
      toast.error('Please enter a time slot');
      return;
    }
    setSettings({ ...settings, time_slots: [...settings.time_slots, cleaned] });
    setNewSlot('');
  };
  const removeTimeSlot = (i) => setSettings({ ...settings, time_slots: settings.time_slots.filter((_, idx) => idx !== i) });
  const clearAllTimeSlots = () => {
    if (!settings.time_slots.length) return toast.info('No time slots to clear');
    if (window.confirm('Delete all time slots?')) {
      setSettings({ ...settings, time_slots: [] });
      toast.success('All time slots cleared');
    }
  };
  const resetDefaultSlots = () => {
    setSettings({ ...settings, time_slots: [...DEFAULT_SLOTS] });
    toast.success('Default time slots loaded');
  };

  if (user?.role !== 'super_admin') {
    return <div className="p-8 text-center"><p className="text-gray-600">Only Super Admins can access this page.</p></div>;
  }
  if (loading) return <div className="p-8 text-center"><p className="text-gray-600">Loading settings...</p></div>;

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="event-settings-page">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#193460] flex items-center gap-2">
          <Settings className="w-7 h-7" /> Event & Form Settings
        </h2>
        <p className="text-gray-600 mt-1">Configure the next repair café event.</p>
      </div>

      <div className="bg-white rounded-lg border-2 border-gray-200 p-6 space-y-6">
        {/* Event schedule CRUD — moved here from the standalone Schedule
            tab. The soonest future entry is auto-promoted to the public
            home page and booking form. */}
        <EventScheduleSection />

        {/* Event time (configuration knob, separate from schedule) */}
        <div className="space-y-4 pt-4 border-t">
          <h3 className="text-lg font-semibold text-[#193460] flex items-center gap-2">
            <Clock className="w-5 h-5" /> Event Time
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="event_time">Default time displayed on the public site</Label>
              <Input id="event_time" data-testid="event-time-input"
                value={settings.event_time || ''}
                onChange={(e) => setSettings({ ...settings, event_time: e.target.value })}
                placeholder="11:00 AM - 3:30 PM" className="mt-1" />
              <p className="text-xs text-gray-500 mt-1">
                Defaults to <code>11:00 AM - 3:30 PM</code> when blank.
              </p>
            </div>
          </div>
        </div>

        {/* Time Slots */}
        <div className="space-y-4 pt-4 border-t">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-lg font-semibold text-[#193460] flex items-center gap-2">
              <Clock className="w-5 h-5" /> Available Time Slots
            </h3>
            <div className="flex gap-2">
              <Button onClick={resetDefaultSlots} variant="outline" size="sm"
                data-testid="reset-default-slots-btn"
                className="border-[#193460] text-[#193460] hover:bg-[#193460] hover:text-white">
                <RotateCcw className="w-4 h-4 mr-2" /> Load Default Slots
              </Button>
              {settings.time_slots.length > 0 && (
                <Button onClick={clearAllTimeSlots} variant="outline" size="sm"
                  data-testid="clear-all-slots-btn"
                  className="border-red-300 text-red-600 hover:bg-red-50">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete All
                </Button>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Input value={newSlot} data-testid="new-slot-input"
              onChange={(e) => setNewSlot(e.target.value)}
              placeholder='e.g., 11:00 - 11:45'
              onKeyPress={(e) => e.key === 'Enter' && addTimeSlot()} />
            <Button onClick={addTimeSlot} data-testid="add-slot-btn"
              className="bg-[#E87B6A] hover:bg-[#d66a59]">
              <Plus className="w-4 h-4 mr-2" /> Add Slot
            </Button>
          </div>
          <p className="text-xs text-gray-500 italic">Format: "11:00 - 11:45"</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {settings.time_slots.map((slot, index) => (
              <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded border" data-testid={`slot-${index}`}>
                <span className="text-sm">{slot}</span>
                <button onClick={() => removeTimeSlot(index)} className="text-red-600 hover:text-red-800" data-testid={`remove-slot-${index}`}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          {settings.time_slots.length === 0 && (
            <p className="text-sm text-gray-500 italic">No time slots configured. Add some above or click "Load Default Slots".</p>
          )}
        </div>

        {/* Capacity is now derived automatically from the Volunteer roster
            — each repairer marked "Repairer" in the Users tab adds one
            seat per slot for their category. No admin knob needed. */}
        <div className="space-y-2 pt-4 border-t">
          <h3 className="text-lg font-semibold text-[#193460] flex items-center gap-2">
            <Users className="w-5 h-5" /> Slot Capacity
          </h3>
          <div className="bg-blue-50 border border-blue-200 rounded p-3">
            <p className="text-sm text-blue-800 leading-relaxed">
              <strong>Capacity is rota-driven.</strong> Capacity for each slot
              equals the number of Repairers you've placed on the rota for
              that slot on the <em>Schedule</em> tab. An empty rota means 0
              bookings for that event — assign volunteers on Schedule to open
              the booking form.
            </p>
          </div>

          {/* Active repair categories. Editing happens on the Categories
              tab; we surface the list here so admins can see which
              categories the booking form currently exposes to customers
              and how they map to the rota. */}
          <div
            className="mt-3 border border-gray-200 rounded p-3 bg-white"
            data-testid="event-settings-categories-summary"
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-[#193460]">
                Active repair categories ({categories.length})
              </h4>
              <span className="text-xs uppercase tracking-wider text-gray-500 bg-gray-100 px-2 py-1 rounded">
                Manage on Categories tab
              </span>
            </div>
            {categories.length === 0 ? (
              <p className="text-sm text-gray-500 italic">
                No active categories. Add some on the Categories tab — the
                booking form will be empty until at least one is active.
              </p>
            ) : (
              <ul className="flex flex-wrap gap-2">
                {categories.map((c) => (
                  <li
                    key={c.id}
                    data-testid={`event-settings-category-${c.id}`}
                    className="inline-flex items-center px-3 py-1 rounded-full bg-[#193460]/5 border border-[#193460]/20 text-sm text-[#193460]"
                  >
                    {c.name}
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-gray-500 mt-2 leading-relaxed">
              Per-category capacity at booking time is computed from each
              repairer's <em>category coverage</em> on the Users tab plus
              the <em>Schedule</em> rota for that event. There is no
              standalone "category × capacity" knob.
            </p>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn"
            className="bg-[#E87B6A] hover:bg-[#d66a59] text-white px-6">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
}

